/**
 * Скриншоты из банка — общие куски: плитка выбора, миниатюры при чтении,
 * плашка «со скриншотов» тем же блоком, что «Умное разделение чека»
 * (docs/design/bank-shots-2026-10-26).
 */
import { useEffect, useMemo } from 'react';
import { Icon } from './icon.js';
import { CloseX } from './close-icon.js';

/** Файлы из поля — массивом; поле очищается, чтобы тот же файл можно было выбрать снова. */
const pick = (e: React.ChangeEvent<HTMLInputElement>): File[] => {
  const files = [...(e.target.files ?? [])];
  e.target.value = '';
  return files;
};

/** Широкая плитка: выбор одного или нескольких скриншотов из галереи. */
export function ShotTile({ title, sub, disabled, onFiles }: {
  title: string; sub: string; disabled?: boolean; onFiles: (files: File[]) => void;
}) {
  return (
    <div className="addtiles shotfirst">
      <label className={`addtile wide${disabled ? ' off' : ''}`}>
        <span className="addtile-i"><Icon name="images" size={22} /></span>
        <span className="t"><b>{title}</b><span>{sub}</span></span>
        <input type="file" accept="image/*" multiple className="filehit" disabled={disabled}
          onChange={(e) => { const f = pick(e); if (f.length) onFiles(f); }} />
      </label>
    </div>
  );
}

/**
 * Пока читаются — карточка на месте плитки: по миниатюрам идёт полоса,
 * внизу бежит полоска. Строки серым текстом с неподвижными миниатюрами
 * владелец не заметил (27.10.2026): казалось, что ничего не происходит.
 */
export function ShotReading({ files, hint }: { files: readonly File[]; hint: string }) {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  const n = files.length;
  return (
    <div className="shotread" role="status">
      <div className="shotthumbs" aria-hidden="true">
        {urls.slice(0, 3).map((u) => <span key={u} className="shotthumb"><img src={u} alt="" /><i /></span>)}
        {n > 3 && <span className="shotmore">+{n - 3}</span>}
      </div>
      <div className="shotreadtext">
        <b><span className="shotspin" aria-hidden="true" />Читаю {n === 1 ? 'скриншот' : `${n} ${n < 5 ? 'скриншота' : 'скриншотов'}`}</b>
        <span>{hint}</span>
      </div>
      <div className="shotbar" aria-hidden="true"><i /></div>
    </div>
  );
}

/** Плашка «со скриншотов»: что нашлось и что проверить; кнопка — ещё скриншот. */
export function ShotPlate({ title, text, action, onFiles, onClose, busy }: {
  title: string; text: string; action?: string; onFiles?: (files: File[]) => void; onClose?: () => void; busy?: boolean;
}) {
  return (
    <div className="rcsplit plate" role="status">
      <div className="rcsplithead">
        <span className="rcsplitico"><Icon name="images" size={13} /></span>
        <div className="rcsplittext"><b>{title}</b><p>{text}</p></div>
      </div>
      {action && onFiles && (
        <label className={`rcsplitok${busy ? ' off' : ''}`}>
          {busy ? 'Читаю…' : action}
          <input type="file" accept="image/*" multiple className="filehit" disabled={busy}
            onChange={(e) => { const f = pick(e); if (f.length) onFiles(f); }} />
        </label>
      )}
      {onClose && <button className="rcsplitx" aria-label="Скрыть" onClick={onClose}><CloseX /></button>}
    </div>
  );
}
